import axios from 'axios'
import toast from 'react-hot-toast'
import { executeRecaptcha } from '../utils/recaptcha'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://dev-api.tabowe.com',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// ── Request interceptor — attach Bearer token ──────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('tabowe_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Auth-flow endpoints handle their own errors on-screen. A 401 from these is a
// bad-credentials / expired-token *attempt*, not an expired session — so the
// global "clear + redirect to /login" must NOT fire (it reloaded the page and
// swallowed the "Invalid credentials" message on the login screen).
const AUTH_FLOW_PATHS = [
  '/auth/login', '/auth/register', '/auth/forgot-password',
  '/auth/reset-password', '/auth/verify-otp', '/auth/verify-2fa', '/auth/resend',
]

// ── Response interceptor — handle 401 & 403 globally ────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    const message = error.response?.data?.message
    const url = error.config?.url || ''
    const isAuthFlow = AUTH_FLOW_PATHS.some((p) => url.includes(p))

    if (status === 401 && !isAuthFlow) {
      // Genuine expired session on an authenticated request → sign out.
      localStorage.removeItem('tabowe_token')
      localStorage.removeItem('tabowe_user')
      window.location.href = '/login'
    }

    if (status === 403) {
      // Banned user — clear session and redirect to login
      if (message?.includes('suspended')) {
        toast.error('Your account has been suspended. Contact support.')
        localStorage.removeItem('tabowe_token')
        localStorage.removeItem('tabowe_user')
        setTimeout(() => { window.location.href = '/login' }, 1500)
      } else {
        // Generic forbidden (e.g. non-admin accessing admin routes)
        toast.error(message || 'You do not have permission to perform this action.')
      }
    }

    return Promise.reject(error)
  }
)

export default api

// The login challenge token (2FA / unverified-email OTP) is stored separately
// from the real session token so AuthContext never treats it as a live session.
// These few endpoints must send it explicitly since the interceptor only
// attaches `tabowe_token`.
const challengeAuth = () => {
  const t = localStorage.getItem('tabowe_challenge')
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {}
}

// ── Auth endpoints ──────────────────────────────────────
// register / login / forgot-password automatically attach a v3 reCAPTCHA
// token to the request body. executeRecaptcha() resolves to null when
// reCAPTCHA isn't configured platform-wide (or RECAPTCHA_FORCE_OFF=true
// on the box), so dev/local works without any captcha setup.
const withRecaptcha = async (action, data) => {
  const recaptcha_token = await executeRecaptcha(action)
  return { ...data, recaptcha_token }
}

export const authAPI = {
  register:      async (data)  => api.post('/auth/register',        await withRecaptcha('register',        data)),
  login:         async (data)  => api.post('/auth/login',           await withRecaptcha('login',           data)),
  logout:        ()            => api.post('/auth/logout'),
  me:            ()            => api.get('/auth/me'),
  forgotPassword:async (email) => api.post('/auth/forgot-password', await withRecaptcha('forgot_password', { email })),
  resetPassword: (data)        => api.post('/auth/reset-password', data),
  googleLogin:   ()            => api.get('/auth/google').then(res => res.data.data.url),
  refresh:       ()            => api.post('/auth/refresh'),
  verifyEmail:   (token)       => api.get('/auth/verify-email', { params: { token } }),
  resendVerification: ()       => api.post('/auth/resend-verification'),
  verifyOtp:     (code)        => api.post('/auth/verify-otp', { code }, challengeAuth()),
  resendOtp:     ()            => api.post('/auth/resend-otp', {}, challengeAuth()),
}

// ── Contact form ────────────────────────────────────────
export const contactAPI = {
  send: async (data) => api.post('/contact', await withRecaptcha('contact', data)),
}

// ── Groups endpoints ────────────────────────────────────
export const groupsAPI = {
  list:          ()              => api.get('/groups'),
  get:           (id)            => api.get(`/groups/${id}`),
  create:        (data)          => api.post('/groups', data),
  update:        (id, data)      => api.put(`/groups/${id}`, data),
  delete:        (id)            => api.delete(`/groups/${id}`),
  members:       (id)            => api.get(`/groups/${id}/members`),
  addMember:     (id, data)      => api.post(`/groups/${id}/members`, data),
  removeMember:  (id, userId)    => api.delete(`/groups/${id}/members/${userId}`),
  balances:      (id)            => api.get(`/groups/${id}/balances`),
  // Spending & budget caps
  spending:      (id, params)    => api.get(`/groups/${id}/spending`, { params }),
  getBudgets:    (id)            => api.get(`/groups/${id}/budgets`),
  setBudget:     (id, data)      => api.post(`/groups/${id}/budgets`, data),
  deleteBudget:  (id, budgetId)  => api.delete(`/groups/${id}/budgets/${budgetId}`),
  transferAdmin: (id, userId)   => api.post(`/groups/${id}/transfer-admin`, { user_id: userId }),
  promoteAdmin:  (id, userId)   => api.post(`/groups/${id}/promote-admin`, { user_id: userId }),
  demoteAdmin:   (id, userId)   => api.post(`/groups/${id}/demote-admin`, { user_id: userId }),
  activity:      (id)            => api.get(`/groups/${id}/activity`),
  createInviteLink: (id, opts)  => api.post(`/groups/${id}/invite-link`, opts || {}),
  joinViaLink:   (token)        => api.post(`/groups/join/${token}`),
}

// ── Expenses endpoints ──────────────────────────────────
export const expensesAPI = {
  list:          (params)        => api.get('/expenses', { params }),
  get:           (id)            => api.get(`/expenses/${id}`),
  create:        (data)          => api.post('/expenses', data),
  update:        (id, data)      => api.put(`/expenses/${id}`, data),
  delete:        (id)            => api.delete(`/expenses/${id}`),
  byGroup:       (groupId, p)    => api.get(`/groups/${groupId}/expenses`, { params: p }),
  comments:      (id)            => api.get(`/expenses/${id}/comments`),
  addComment:    (id, data)      => api.post(`/expenses/${id}/comments`, data),
  uploadReceipt: (id, file)      => {
    const formData = new FormData()
    formData.append('receipt', file)
    return api.post(`/expenses/${id}/receipt`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  // Receipts are behind an authorised endpoint on the private disk, so they
  // must be fetched with the bearer token — a bare <img>/<a href> would 401.
  receipt:       (id)            => api.get(`/expenses/${id}/receipt`, { responseType: 'blob' }),
  deleteReceipt: (id)            => api.delete(`/expenses/${id}/receipt`),
  getItems:      (id)            => api.get(`/expenses/${id}/items`),
  saveItems:     (id, items)     => api.post(`/expenses/${id}/items`, { items }),
  assignItem:    (expId, itemId, userId) => api.put(`/expenses/${expId}/items/${itemId}/assign`, { user_id: userId }),
  checkDuplicate:(params)        => api.get('/expenses/check-duplicate', { params }),
}

// ── Friends endpoints ───────────────────────────────────
export const friendsAPI = {
  list:          ()              => api.get('/friends'),
  search:        (q)             => api.get('/users/search', { params: { q } }),
  add:           (data)          => api.post('/friends', data),
  remove:        (id)            => api.delete(`/friends/${id}`),
  balanceWith:   (id)            => api.get(`/friends/${id}/balance`),
}

// ── Settlements endpoints ───────────────────────────────
export const settlementsAPI = {
  list:          (params)        => api.get('/settlements', { params }),
  outstanding:   (params)        => api.get('/settlements/outstanding', { params }),
  create:        (data)          => api.post('/settlements', data),
  batch:         (settlements)   => api.post('/settlements/batch', { settlements }),
  get:           (id)            => api.get(`/settlements/${id}`),
  byGroup:       (groupId)       => api.get(`/groups/${groupId}/settlements`),
}

// ── Receipt scan endpoints ──────────────────────────────
export const receiptAPI = {
  scan: (file) => {
    const formData = new FormData()
    formData.append('receipt', file)
    return api.post('/receipts/scan', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
}

// ── Recurring expenses endpoints ────────────────────────
export const recurringAPI = {
  list:    ()          => api.get('/recurring-expenses'),
  get:     (id)        => api.get(`/recurring-expenses/${id}`),
  create:  (data)      => api.post('/recurring-expenses', data),
  update:  (id, data)  => api.put(`/recurring-expenses/${id}`, data),
  delete:  (id)        => api.delete(`/recurring-expenses/${id}`),
}

// ── Expense templates endpoints ─────────────────────────
export const expenseTemplatesAPI = {
  list:   ()         => api.get('/expense-templates'),
  get:    (id)       => api.get(`/expense-templates/${id}`),
  create: (data)     => api.post('/expense-templates', data),
  update: (id, data) => api.put(`/expense-templates/${id}`, data),
  delete: (id)       => api.delete(`/expense-templates/${id}`),
}

// ── Currency endpoints ──────────────────────────────────
export const currencyAPI = {
  rates:   (base)         => api.get('/currency/rates', { params: { base } }),
  convert: (amount, from, to) => api.post('/currency/convert', { amount, from, to }),
}

// ── Billing endpoints ──────────────────────────────────
export const billingAPI = {
  status:   ()                       => api.get('/billing/status'),
  checkout: (plan, interval = 'monthly') => api.post('/billing/checkout', { plan, interval }),
  portal:   ()                       => api.post('/billing/portal'),
}

// ── 2FA endpoints ──────────────────────────────────────
export const twoFactorAPI = {
  status:  ()         => api.get('/2fa/status'),
  setup:   ()         => api.post('/2fa/setup'),
  confirm: (code)     => api.post('/2fa/confirm', { code }),
  disable: (password) => api.post('/2fa/disable', { password }),
  // Sends the limited challenge token (tabowe_challenge) explicitly as bearer.
  verify:  (code)     => api.post('/2fa/verify', { code }, challengeAuth()),
}

// ── Profile endpoints ───────────────────────────────────
export const profileAPI = {
  uploadAvatar: (file) => {
    const formData = new FormData()
    formData.append('avatar', file)
    return api.post('/profile/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  deleteAvatar: () => api.delete('/profile/avatar'),
  deleteAccount: (password) => api.post('/profile/delete-account', { password }),
}

// ── Notifications endpoints ─────────────────────────────
export const notificationsAPI = {
  list:         ()   => api.get('/notifications'),
  unreadCount:  ()   => api.get('/notifications/unread-count'),
  markAsRead:   (id) => api.post(`/notifications/${id}/read`),
  markAllAsRead:()   => api.post('/notifications/read-all'),
}

// ── Export endpoints ────────────────────────────────────
export const exportAPI = {
  expenses:      (params) => api.get('/export/expenses', { params, responseType: 'blob' }),
  settlements:   ()       => api.get('/export/settlements', { responseType: 'blob' }),
  groupExpenses: (groupId)=> api.get(`/export/groups/${groupId}/expenses`, { responseType: 'blob' }),
  account:       ()       => api.get('/export/account', { responseType: 'blob' }),
}

export function downloadBlob(response, fallbackName = 'export.csv') {
  const disposition = response.headers['content-disposition']
  const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] || fallbackName
  // Carry the server's content type onto the Blob. Without it every download
  // is application/octet-stream, which stops the browser previewing a PDF and
  // can make it append its own extension.
  const type = response.headers['content-type'] || 'application/octet-stream'
  const url = window.URL.createObjectURL(new Blob([response.data], { type }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

// ── Insights endpoints ──────────────────────────────────
export const insightsAPI = {
  personal:    (params) => api.get('/insights/personal', { params }),
  groupReport: (groupId, params) => api.get(`/insights/groups/${groupId}`, { params }),
  // params accepts { from, to } to scope the report to a date window — pass the
  // same range shown on screen so the PDF matches it.
  groupPdf:    (groupId, params) => api.get(`/insights/groups/${groupId}/pdf`, { params, responseType: 'blob' }),
}

// ── Push subscription endpoints ────────────────────────
export const pushAPI = {
  subscribe:   (sub) => api.post('/push/subscribe', sub),
  unsubscribe: (endpoint) => api.post('/push/unsubscribe', { endpoint }),
}

// ── Payment handles endpoints ──────────────────────────
export const paymentHandlesAPI = {
  list:        ()         => api.get('/payment-handles'),
  save:        (type, handle) => api.post('/payment-handles', { type, handle }),
  delete:      (id)       => api.delete(`/payment-handles/${id}`),
  getLink:     (userId, amount, currency) => api.get(`/payment-handles/${userId}/link`, { params: { amount, currency } }),
}

// ── Dashboard endpoints ─────────────────────────────────
export const dashboardAPI = {
  summary:       ()              => api.get('/dashboard/summary'),
  activity:      (params)        => api.get('/dashboard/activity', { params }),
  balances:      ()              => api.get('/dashboard/balances'),
  spending:      (params)        => api.get('/dashboard/spending', { params }),
}
